import React from 'react';
import { Grid, Paper, Typography, Divider, Chip, Box } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import StadiumIcon from '@mui/icons-material/Stadium';
import MoneyIcon from '@mui/icons-material/Money';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly';
import SportsIcon from '@mui/icons-material/Sports';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import { Place } from '@/types/place';
import theme from './Theme';

interface PlaceInfoProps {
  place: Place;
}

/**
 * Place information component displaying description, type, fee, age category, tags, equipment.
 */
export const PlaceInfo: React.FC<PlaceInfoProps> = ({ place }) => {
  return (
    <Paper sx={{ p: 3, mb: 2, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        About this place
      </Typography>
      <Grid container spacing={2}>
        {/* Description */}
        <Grid item xs={12}>
          <Typography>
            <DescriptionIcon sx={{ color: theme.palette.accent.main, verticalAlign: 'middle', mr: 1 }} />
            <strong>Description:</strong> {place.description || 'N/A'}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        {/* Type and Fee */}
        <Grid item xs={12} md={6}>
          <Typography>
            <StadiumIcon sx={{ color: theme.palette.accent.main, verticalAlign: 'middle', mr: 1 }} />
            <strong>Type:</strong> {place.type || 'N/A'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography>
            <MoneyIcon sx={{ color: theme.palette.accent.main, verticalAlign: 'middle', mr: 1 }} />
            <strong>Fee:</strong> {(place as any).fee?.price || 'N/A'} ({(place as any).fee?.price_type || 'N/A'})
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        {/* Age Category and Tags */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1 }}>
            <AccessibilityIcon sx={{ color: theme.palette.accent.main, verticalAlign: 'middle', mr: 1 }} />
            <strong>Age category:</strong>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {place.age_category && place.age_category.length > 0 ? (
              place.age_category.map((category, index) => (
                <Chip key={index} label={category} size="small" />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">N/A</Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1 }}>
            <LocalOfferIcon sx={{ color: theme.palette.accent.main, verticalAlign: 'middle', mr: 1 }} />
            <strong>Tags:</strong>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {place.tags && place.tags.length > 0 ? (
              place.tags.map((tag, index) => (
                <Chip key={index} label={tag} size="small" color="primary" variant="outlined" />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">N/A</Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        {/* Kid Friendly and Disability Friendly */}
        <Grid item xs={12} md={6}>
          <Typography>
            <ChildFriendlyIcon sx={{ color: theme.palette.accent.main, verticalAlign: 'middle', mr: 1 }} />
            <strong>Kid Friendly:</strong> {place.kid_friendly ? 'Yes' : 'No'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography>
            <AccessibilityIcon sx={{ color: theme.palette.accent.main, verticalAlign: 'middle', mr: 1 }} />
            <strong>Disability Friendly:</strong> {place.disability_friendly ? 'Yes' : 'No'}
          </Typography>
        </Grid>

        {/* Equipment Description */}
        {(place as any).equipment?.equipment_description && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography>
                <StickyNote2Icon sx={{ color: theme.palette.accent.main, verticalAlign: 'middle', mr: 1 }} />
                <strong>Equipment Notes:</strong> {(place as any).equipment.equipment_description}
              </Typography>
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
};
